import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { CADCurrencyConverter } from "./currency";
import { ItemUOMSelectResult, calculateBaseQuantity, itemUOMSelect } from "./item-uoms";
import { Item, ItemSelectResult, itemSelect, processItem } from "./items";

export interface Transfer extends Omit<TransferSelectResult, "lines"> {
  allLines: TransferLineItem[];
  salesOrderRackLines: TransferLineItem[];
}

export interface TransferLineItem extends Omit<TransferLineItemSelectResult, "item"> {
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  item: Item;
}

export interface TransferSelectResult extends Pick<Tables<"Transfer">, "id" | "number" | "date" | "comment"> {
  lines: TransferLineItemSelectResult[];
}

export interface TransferLineItemSelectResult extends Pick<
  Tables<"TransferLineItem">,
  "id" | "lineNumber" | "description"
> {
  item: ItemSelectResult;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  itemUOM: ItemUOMSelectResult | null;
  fromBin: Pick<Tables<"Bin">, "id" | "number"> | null;
  toBin: Pick<Tables<"Bin">, "id" | "number"> | null;
}

const transferSelect = `
id,
number,
date,
comment,
lines:TransferLineItem!inner(
  id,
  lineNumber,
  item:Item(${itemSelect}),
  job:Job(id, name),
  description,
  uomQuantity:quantity,
  itemUOM:ItemUOM(${itemUOMSelect}),
  fromBin:Bin!TransferLineItem_fromBinId_fkey(id, number),
  toBin:Bin!TransferLineItem_toBinId_fkey(id, number)
)
`;

export async function getTransfers(binId: number, CADCurrencyConverter: CADCurrencyConverter): Promise<Transfer[]> {
  // Get all transfer ids with any non-deleted line item to or from the sales order rack bin
  const transferIds = new Set(
    (
      await supabase
        .from("TransferLineItem")
        .select("transferId")
        .eq("deleted", false)
        .or(`fromBinId.eq.${binId.toFixed()},toBinId.eq.${binId.toFixed()}`)
        .throwOnError()
    ).data.map((x) => x.transferId),
  );

  // Get all non-deleted transfers with the matched transfer ids
  const queryResult = (
    await supabase
      .from("Transfer")
      .select<typeof transferSelect, TransferSelectResult>(transferSelect)
      .in("id", [...transferIds])
      .eq("deleted", false)
      .eq("lines.deleted", false)
      .order("date", { ascending: false })
      .order("lineNumber", { referencedTable: "lines", ascending: true })
      .throwOnError()
  ).data;

  // Do some additional client-side processing on the results before returning
  return queryResult.map((po) => processTransfer(po, binId, CADCurrencyConverter));
}

function processTransfer(
  { lines: rawLines, ...transfer }: TransferSelectResult,
  binId: number,
  CADCurrencyConverter: CADCurrencyConverter,
): Transfer {
  const allLines = rawLines.map((line) => processTransferLineItem(line, CADCurrencyConverter));
  const salesOrderRackLines = allLines.filter((x) => x.fromBin?.id === binId || x.toBin?.id === binId);
  return { ...transfer, allLines, salesOrderRackLines };
}

function processTransferLineItem(
  line: TransferLineItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): TransferLineItem {
  return {
    ...line,
    baseQuantity: calculateBaseQuantity(line.uomQuantity, line.itemUOM),
    item: processItem(line.item, CADCurrencyConverter),
  };
}
